import { growthbook } from '@app/core/integrations/growthbook';
import debugFn from 'debug';
import Hls, { HlsConfig } from 'hls.js';
import Player from 'video.js/dist/types/player';
import { setupHlsVodSeekJump } from '../../setup-hls-vod-seek-jump';
import { HlsJsPreset } from '../../types';

const debug = debugFn('vodPreset');

const BYTE_RANGE_BUFFER_FLAG = 'segments__hls_byte_range_segments_vod';

const baseConfig: Partial<HlsConfig> = {
  enableWorker: true, // keep main thread snappy
  testBandwidth: false, // Skip ABR probe fragment; single quality only (default: true)
  startLevel: 0, // Pick the only rendition immediately (default: undefined)
  maxStarvationDelay: 2, // React faster when buffer runs dry after a seek (default: 4)
  maxLoadingDelay: 2, // Abort slow loads sooner after a seek (default: 4)
};

/** Buffer tuning for VOD byte-range segment playlists. */
const byteRangeBufferConfig: Partial<HlsConfig> = {
  backBufferLength: 30,

  // Forward buffer for byte-range fragments.
  maxBufferLength: 30,
  maxMaxBufferLength: 100,
  maxBufferSize: 30 * 1000 * 1000,
  frontBufferFlushThreshold: 60,
};

/** Buffer tuning for standard VOD (30s segments, scrub-heavy, single quality). */
const defaultBufferConfig: Partial<HlsConfig> = {
  backBufferLength: 0, // No back buffer; users seek, not linear rewind
  maxBufferLength: 15, // Target ~half a segment ahead (default: 30)
  maxMaxBufferLength: 30, // Hard cap at 1 segment (default: 600)
  maxBufferSize: 30 * 1000 * 1000, // ~1 segment in bytes for 10–20MB chunks (default: 60MB)
  frontBufferFlushThreshold: 30, // Flush stale forward buffer on seek after 1 segment (default: Infinity)
};

/**
 * VOD (Video On Demand) preset for HLS.js.
 * Optimized for playback of pre-recorded content with progressive loading.
 * Byte-range buffer settings apply only when {@link BYTE_RANGE_BUFFER_FLAG} is on.
 */
export function getVodPreset(): HlsJsPreset {
  const useByteRangeBufferSettings = growthbook.isOn(BYTE_RANGE_BUFFER_FLAG);

  if (useByteRangeBufferSettings) {
    debug('using byte-range buffer config (%s)', BYTE_RANGE_BUFFER_FLAG);
  } else {
    debug('using default buffer config');
  }

  return {
    config: {
      ...baseConfig,

      // 1. DISABLE ALL LIVE LOGIC
      // This ensures the player never tries to 'snap' to the end.
      lowLatencyMode: false,

      // 2. SPEED & STARTUP
      autoStartLoad: true,
      startFragPrefetch: true,
      progressive: true, // Start playing the 30s segment before it fully downloads

      ...(useByteRangeBufferSettings ? byteRangeBufferConfig : defaultBufferConfig),
    },
    setup: (hls: Hls, player: Player) => setupHlsVodSeekJump(hls, player),
  };
}
